import React from 'react';
import { motion } from 'framer-motion';
import { CalendarIcon, StarIcon } from '@heroicons/react/24/outline';
import {
  Controller,
  Control,
  FieldErrors,
  UseFormRegister
} from 'react-hook-form';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { berRatings } from '../../../components/property/property-card/PropertyCardTypes';
import { ListingFormData } from '../types';

interface AdditionalInformationProps {
  register: UseFormRegister<ListingFormData>;
  control: Control<ListingFormData>;
  errors: FieldErrors<ListingFormData>;
}

const AdditionalInformation: React.FC<AdditionalInformationProps> = ({
  register,
  control,
  errors
}) => {
  return (
    <motion.div
      key="singleStep4"
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      className="space-y-6"
    >
      {/* Progress indicator */}
      <div className="bg-indigo-50 border-l-4 border-indigo-600 p-4 rounded-r-lg mb-6">
        <p className="text-sm text-indigo-800">
          <strong>Step 4 of 6:</strong> Quick details • Just{' '}
          <strong>~30 seconds</strong> left!
        </p>
      </div>

      <h2 className="text-2xl font-semibold mb-6 flex items-center">
        <CalendarIcon className="w-6 h-6 mr-2 text-blue-500" />
        Additional Information
      </h2>
      <div className="space-y-6">
        <div>
          <label className="block mb-2 font-medium text-gray-700">
            Available From <span className="text-red-500">*</span>
          </label>
          <p className="text-sm text-gray-500 mb-2">
            When can tenants move in? Properties "Available Immediately" get{' '}
            <strong>50% more views</strong>
          </p>
          <Controller
            control={control}
            name="availableFrom"
            rules={{
              required: 'Available from date is required',
              validate: {
                notPast: (value) => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  if (value) {
                    return value >= today || 'Date cannot be in the past';
                  }
                  return 'Date is required';
                }
              }
            }}
            render={({ field }) => (
              <DatePicker
                onChange={(date) => field.onChange(date)}
                selected={field.value}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition duration-300 ${
                  errors.availableFrom ? 'border-red-500' : 'border-gray-300'
                }`}
                minDate={new Date()}
                placeholderText="Select availability date"
                dateFormat="dd/MM/yyyy"
              />
            )}
          />
          {errors.availableFrom && (
            <span className="text-red-500 text-sm">
              {errors.availableFrom.message}
            </span>
          )}
        </div>
        <div>
          <label className="block mb-2 font-medium text-gray-700">
            <StarIcon className="w-5 h-5 inline-block mr-1 text-blue-500" />
            BER Rating <span className="text-red-500">*</span>
          </label>
          <p className="text-sm text-gray-500 mb-2">
            Higher BER ratings (A-C) attract more
            eco-conscious tenants
          </p>
          <select
            {...register('berRating', {
              required: 'BER rating is required'
            })}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition duration-300 ${
              errors.berRating ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="">Select BER rating</option>
            {berRatings.map((rating: string) => (
              <option key={rating} value={rating}>
                {rating}
              </option>
            ))}
          </select>
          {errors.berRating && (
            <span className="text-red-500 text-sm">
              {errors.berRating.message}
            </span>
          )}
        </div>
        <div>
          <label className="block mb-2 font-medium text-gray-700">
            Virtual Tour URL (optional)
          </label>
          <p className="text-sm text-gray-500 mb-2">
            Add a 360° tour or video. Listings with virtual tours get{' '}
            <strong>3x more engagement</strong>
          </p>
          <input
            {...register('virtualTourUrl', {
              pattern: {
                value:
                  /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z]{2,})+(\/[\w\-.~:?#@!$&'()*+,;=]*)*\/?$/,
                message: 'Enter a valid virtual tour URL'
              }
            })}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition duration-300 ${
              errors.virtualTourUrl ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="e.g., https://matterport.com/your-tour or YouTube link"
          />
          {errors.virtualTourUrl && (
            <span className="text-red-500 text-sm">
              {errors.virtualTourUrl.message}
            </span>
          )}
        </div>

        {/* Help text */}
        <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded-r-lg">
          <p className="text-sm text-green-800">
            <strong>🎉 Almost done!</strong> Just photos and review remaining.
            Your listing will be live in minutes!
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default AdditionalInformation;
